import { Select, Textarea } from "@mantine/core";
import { map } from "lodash";
import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover";
import { TableData, TableRow } from "../../../components/Table/types";
import {
  DeskCollectionResponse,
  UpdateDeskCollectionInput,
} from "../../../graphql/generated.ts";
import {
  DeskCollection,
  deskCollectionResponseOptions,
} from "../../../types/deskCollection/deskCollection.ts";
import { toLocalDate } from "../../../utils/date.ts";

interface Props {
  data: DeskCollection[] | null | undefined;
  onInlineEdit: (id: number, value: UpdateDeskCollectionInput) => void;
}

export function useDeskCollectionDashboardTableData({
  data,
  onInlineEdit,
}: Props): TableData {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "collectionDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "collectionDate",
          }}
        >
          Desk Collection Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "customerName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "name",
          }}
        >
          Customer Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "picName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "picName",
          }}
        >
          PIC Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "phone",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "phone",
          }}
        >
          Phone Number
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "invoiceNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceNumber",
          }}
        >
          Invoice Number
        </DatabaseMetadataPopover>
      ),
      width: "300px",
    },
    {
      accessor: "response",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "response",
          }}
        >
          Response
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "notes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "notes",
          }}
        >
          Notes
        </DatabaseMetadataPopover>
      ),
      width: "350px",
    },
    {
      accessor: "issue",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "DeskCollection",
            fieldName: "issue",
          }}
        >
          Issue
        </DatabaseMetadataPopover>
      ),
      width: "350px",
    },
  ];

  const rows = useMemo(() => {
    return data?.map((item) => {
      return {
        id: item.id,
        collectionDate: toLocalDate(item.collectionDate),
        customerName: item.customer.name,
        picName: item.picName,
        phone: item.phone,
        invoiceNumber: map(item.invoices, "invoiceNumber").join("; "),
        response: (
          <Select
            key={item.id}
            data={deskCollectionResponseOptions}
            value={item.response}
            placeholder="Select Response"
            size="sm"
            withinPortal
            zIndex={100}
            onChange={(value) =>
              onInlineEdit(item.id, {
                response: value as DeskCollectionResponse,
              })
            }
          />
        ),
        issue: (
          <Textarea
            placeholder="Enter Issue"
            rows={4}
            size="sm"
            defaultValue={item.issue ?? undefined}
            onBlur={(event) =>
              onInlineEdit(item.id, {
                issue: event.target.value,
              })
            }
          />
        ),
        notes: (
          <Textarea
            placeholder="Enter Notes"
            rows={4}
            size="sm"
            defaultValue={item.notes ?? undefined}
            onBlur={(event) =>
              onInlineEdit(item.id, {
                notes: event.target.value,
              })
            }
          />
        ),
      };
    });
  }, [data, onInlineEdit]);

  return [columns, rows];
}
